import React, { useState } from "react";
import { connect } from "react-redux";
import { logout, updateNewPassword, useAppSelector } from "@hypexone/uicore";
import { getEnvVariable } from "../../../constants/brandConfig";
import { domain } from "../../../constants/Branding";
import ButtonSpinner from "../../components/shared/ButtonSpinner";

/** Sky's form field, the same as My Profile > Change Password. */
const INPUT_CLASS =
  "h-[30px] w-full rounded-[4px] border border-[#aaa] bg-white px-2 text-[12px] text-black shadow-[inset_0_2px_0_rgba(0,0,0,0.1)] outline-none focus:border-[#666]";

interface ResetPasswordProps {
  updateNewPassword: Function;
  logout: Function;
}

/**
 * First login (and accept-terms) password page. The user can't use the site
 * until it's done, so it carries the site's header and a Sky panel rather than
 * a standalone card, to look like the rest of the site.
 */
const ResetPassword: React.FC<ResetPasswordProps> = (props) => {
  const { updateNewPassword, logout } = props;
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatNewPassword, setRepeatNewPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {langData} = useAppSelector((state) => state.cms);

  // Sky's order: the new password twice, then the current one to authorise it.
  const fields = [
    {
      key: "new_password",
      label: "New Password",
      value: newPassword,
      onChange: setNewPassword,
    },
    {
      key: "repeat_new_password",
      label: "Confirm Password",
      value: repeatNewPassword,
      onChange: (value: string) => {
        setRepeatNewPassword(value);
        setMismatch(false);
      },
    },
    {
      key: "old_password",
      label: "Old Password",
      value: oldPassword,
      onChange: setOldPassword,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[#eee] font-[Tahoma,Helvetica,sans-serif] text-[12px] text-[#1e1e1e]">
      <header className="flex h-[60px] shrink-0 items-center bg-header-gradient px-3 md:h-[74px] md:px-4">
        <img
          src={getEnvVariable(domain, "logo")}
          alt="logo"
          className="h-[40px] object-contain md:h-[52px]"
        />
      </header>
      <div className="h-[6px] shrink-0 bg-sub-header-gradient" />

      <div className="flex flex-1 justify-center px-3 pt-6 md:pt-10">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (submitting) return;
            if (newPassword !== repeatNewPassword) {
              setMismatch(true);
              return;
            }
            setSubmitting(true);
            try {
              const result = await updateNewPassword({ newPassword, oldPassword });
              // uicore logs out only when the change returns 204; on any other
              // success the user stayed signed in on the first-login token,
              // which still says "reset password" and leaves bets locked.
              // Its own logout clears the token, so this never runs twice.
              if (
                result?.meta?.requestStatus === "fulfilled" &&
                sessionStorage.getItem("jwt_token")
              ) {
                await logout();
              }
            } finally {
              setSubmitting(false);
            }
          }}
          className="h-fit w-full max-w-[460px] overflow-hidden rounded-[4px] border border-[#ccc] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.2)]"
        >
          <h2 className="bg-[#243a48] px-3 py-[7px] text-[13px] font-bold text-white">
            {langData?.["set_password"] ?? "Change Password"}
          </h2>

          <div className="p-3">
            <p className="mb-3 text-[#666]">
              {langData?.["password_restrictions"]}
            </p>

            <dl className="grid grid-cols-[140px_1fr] items-center gap-x-3 gap-y-2 max-sm:grid-cols-1 max-sm:gap-y-1">
              {fields.map((field) => {
                const id = `reset-password-${field.key}`;
                const label = langData?.[field.key] ?? field.label;
                return (
                  <React.Fragment key={field.key}>
                    <dt>
                      <label htmlFor={id}>{label}</label>
                    </dt>
                    <dd className="m-0">
                      <input
                        id={id}
                        type="password"
                        className={INPUT_CLASS}
                        placeholder={`Enter ${label}`}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        required
                      />
                      {field.key === "repeat_new_password" && mismatch && (
                        <p className="mt-1 text-[11px] font-bold text-[#d0021b]">
                          {langData?.["password_mismatch_txt"] ?? "Password mismatch"}
                        </p>
                      )}
                    </dd>
                  </React.Fragment>
                );
              })}
            </dl>

            <div className="mt-3 grid grid-cols-[140px_1fr] gap-x-3 gap-y-3 max-sm:grid-cols-1">
              <label className="col-start-2 flex items-center gap-2 max-sm:col-start-1">
                <input
                  type="checkbox"
                  className="accent-[#243a48]"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  required
                />
                {langData?.["accept_tc_txt"]}
              </label>
              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="col-start-2 flex h-[30px] w-[140px] items-center justify-center gap-2 rounded-[4px] border border-[#222] bg-sky-send-gradient text-[12px] font-bold text-skySendText hover:brightness-110 disabled:cursor-wait disabled:opacity-70 max-sm:col-start-1 max-sm:w-full"
              >
                {submitting && <ButtonSpinner />}
                {langData?.["submit"] ?? "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    updateNewPassword: (event: any) => dispatch(updateNewPassword(event)),
    logout: () => dispatch(logout()),
  };
};

export default connect(null, mapDispatchToProps)(ResetPassword);
